import React, { useEffect, useState } from 'react';
import { useLDClient, useFlags } from 'launchdarkly-react-client-sdk';
import { track, trackPageLoad } from '@fff-web/fff-analytics';
import { useUserContext } from '@fff-web/fff-utilities';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '@fff-web/fff-ui-shared/LoadingSpinner';
import { Plans, PriceLockStatus, PriceLockStatusMessage } from '../constants';
import env from '../MyAccount/utils/env';
import { getPriceLock, postPriceLock } from '../../services/subscription';
import LockInRate from './LockInRate';

const LOCK_IN_RATE_REQUESTED = 'Lock In Rate Requested';

const LockInRateContainer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lockedRateStatus, setLockedRateStatus] = useState(
    PriceLockStatus.NOT_REQUESTED
  );
  const [lockedRateStatusMessage, setLockedRateStatusMessage] = useState('');
  const [buttonClicked, setButtonClicked] = useState(false);
  const [flagsReady, setFlagsReady] = useState(false);
  const {
    userInfo: { user },
  } = useUserContext();
  const flags = useFlags();
  const ldClient = useLDClient();
  const location = useLocation();
  const status = new URLSearchParams(location.search).get('status');

  useEffect(() => {
    setIsLoading(true);
    trackPageLoad();
    getPriceLock(status)
      .then((response) => {
        if (response?.status) {
          setLockedRateStatus(response.status);
          setLockedRateStatusMessage(PriceLockStatusMessage[response.status]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    window.fffPactSafe?.init(process.env.REACT_APP_PACT_SAFE_ID);
  }, [window.fffPactSafe]);

  useEffect(() => {
    ldClient.identify({ key: user?.userId }).then(() => setFlagsReady(true));
  }, []);

  const handlePurchase = () => {
    setButtonClicked(true);
    setIsLoading(true);
    postPriceLock()
      .then((response) => {
        setLockedRateStatus(PriceLockStatus.PAYMENT_PENDING);
        setLockedRateStatusMessage(PriceLockStatusMessage.PAYMENT_PENDING);
        const event = { trackName: LOCK_IN_RATE_REQUESTED };
        const isStatusSuccess = response?.status === 200;
        const isStatusFailure = !isStatusSuccess && response?.status !== 204;
        if (isStatusSuccess) {
          track({ ...event, properties: { status: 'success' } });
          const email = user?.email;
          if (window.fffPactSafe) {
            window.fffPactSafe.signContractsForGroup(
              email,
              process.env.REACT_APP_PACT_SAFE_LOCK_RATE_GROUP_KEY
            );
          }
        } else if (isStatusFailure) {
          track({ ...event, properties: { status: 'failure' } });
          setLockedRateStatus(PriceLockStatus.PAYMENT_FAILURE);
          setLockedRateStatusMessage(PriceLockStatusMessage.PAYMENT_FAILURE);
        }
      })
      .catch(() => {
        setLockedRateStatusMessage(
          'Oops something went wrong! Please contact customer service support directly to lock in your price.'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading || !flagsReady) return <LoadingSpinner />;

  const redirectToMyAccount =
    user.planCode === Plans.SEASONAL || !flags.priceLock;

  if (redirectToMyAccount) {
    window.location = `https://${env.hostname}/`;
  }

  return (
    <LockInRate
      handlePurchase={handlePurchase}
      lockedRateStatus={lockedRateStatus}
      subscriptionState={user?.subscriptionState}
      lockedRateStatusMessage={lockedRateStatusMessage}
      buttonClicked={buttonClicked}
    />
  );
};

export default LockInRateContainer;
